import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, LANGUAGE_SWITCHER_LIVE } from '../../i18n';
import { chooseLanguage } from '../../i18n/account';
import { formatTimeAgo } from '../../i18n/format';

export const UserManagement = () => {
    const { t, i18n } = useTranslation();
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userDetails, setUserDetails] = useState(null);
    const [newTokenName, setNewTokenName] = useState('');

    const isAdmin = userDetails?.roles?.some(r => r.toLowerCase() === 'admin') ?? false;
    const isSuperAdmin = userDetails?.roles?.some(r => r.toLowerCase() === 'superadmin') ?? false;

    const loadDevices = async () => {
        const response = await fetch('api/identity/listtokens');
        const data = await response.json();
        setDevices(data);
        setLoading(false);
    }

    const loadUserDetails = async () => {
        const response = await fetch('api/identity/getuser');
        const data = await response.json();
        setUserDetails(data);
    }

    const handleUpdatePassword = async () => {
        const newPassword = document.getElementById('newPassword').value;
        const confirmPassword = document.getElementById('confirmPassword').value;
        if (newPassword !== confirmPassword) {
            alert(t('account.passwordsDoNotMatch'));
            return;
        }
        const response = await fetch('api/identity/updatepassword', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ newPassword: newPassword })
        });
        if (response.ok) {
            alert(t('account.passwordUpdated'));
        } else {
            alert(t('account.passwordUpdateFailed'));
        }
    }

    const handleAddToken = async () => {
        const name = newTokenName.trim();
        if (!name) {
            alert(t('account.tokenNameRequired'));
            return;
        }
        if (devices.some(d => (d.username || '').toLowerCase() === name.toLowerCase())) {
            alert(t('account.tokenNameExists'));
            return;
        }
        const response = await fetch('api/identity/admin/tokens', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ name: name })
        });
        if (response.ok) {
            setNewTokenName('');
            loadDevices();
        } else {
            const message = await response.text();
            alert(message || t('account.addTokenFailed'));
        }
    }

    const handleDeleteToken = async (token, name) => {
        if (!window.confirm(t('account.confirmDeleteToken', { name }))) {
            return;
        }
        const response = await fetch(`api/identity/admin/tokens/${token}`, {
            method: 'DELETE'
        });
        if (response.ok) {
            loadDevices();
        } else {
            const message = await response.text();
            alert(message || t('account.deleteTokenFailed'));
        }
    }

    const handleUpdateNotifications = async () => {
        let enrolment = {};
        enrolment.isEnrolled = document.getElementById('emailNotifications').checked;
        enrolment.email = document.getElementById('email').value;
        const response = await fetch('api/identity/updatenotifications', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                isEnrolled: enrolment.isEnrolled,
                email: enrolment.email
            })
        });
        if (response.ok) {
            alert(t('account.notificationsUpdated'));
        } else {
            alert(t('account.notificationsUpdateFailed'));
        }
    }

    const handleLanguage = async (code) => {
        if (!(await chooseLanguage(code, { signedIn: true }))) {
            alert(t('language.saveFailed'));
        }
    }

    useEffect(() => {
        loadDevices();
        loadUserDetails();
    }, []);

    return (
        <div className="container" style={{ marginTop: 10 }}>
            <div className="row">
                <div className="col-12">
                    <h3>{t('account.deviceManagement')}</h3>
                    <p className="text-muted">
                        {t('account.deviceManagementHelp')}
                    </p>
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>{t('account.columnId')}</th>
                                <th>{t('account.columnToken')}</th>
                                <th>{t('account.columnCreated')}</th>
                                {isAdmin && <th>{t('account.columnActions')}</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {devices.map(user => (
                                <tr key={user.token}>
                                    <td>{user.username}</td>
                                    <td>{user.token}</td>
                                    <td>{formatTimeAgo(user.created)}</td>
                                    {isAdmin && (
                                        <td>
                                            <button
                                                className="btn btn-danger btn-sm"
                                                onClick={() => handleDeleteToken(user.token, user.username)}
                                            >
                                                {t('common.delete')}
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {isAdmin && (
                        <div className="input-group mb-3" style={{ maxWidth: 500 }}>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t('account.tokenNamePlaceholder')}
                                value={newTokenName}
                                onChange={(e) => setNewTokenName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleAddToken(); }}
                            />
                            <button className="btn btn-primary" onClick={handleAddToken} disabled={loading}>
                                {t('account.addToken')}
                            </button>
                        </div>
                    )}
                </div>
                <div className="col-lg-6">
                    <h3>{t('account.userDetails')}</h3>
                    {userDetails ? (
                        <div>
                            <p><strong>{t('account.username')}</strong> {userDetails.username}</p>
                            <p><strong>{t('account.roles')}</strong> {userDetails.roles.join(', ')}</p>
                            <p><strong>{t('account.customer')}</strong> {userDetails.customer}</p>
                            {/*Add update password input*/}
                            <div className="form-group">
                                <label htmlFor="newPassword"><strong>{t('account.updatePasswordLabel')}</strong></label>
                                <input type="password" className="form-control" id="newPassword" placeholder={t('account.newPasswordPlaceholder')} />
                                <input type="password" className="form-control mt-2" id="confirmPassword" placeholder={t('account.confirmPasswordPlaceholder')} />
                                <button className="btn btn-primary mt-2" onClick={handleUpdatePassword} disabled={loading} style={{ marginBottom: 10 }}>{t('account.updatePassword')}</button>
                            </div>
                            <h3><strong>{t('account.surveyNotifications')}</strong></h3>
                            <div className="form-group">
                                <label htmlFor="email"><strong>{t('account.notificationEmail')}</strong></label>
                                <input type="email" className="form-control" id="email" placeholder={t('account.emailPlaceholder')} defaultValue={userDetails.email} />
                                <div className="form-check">
                                    <label htmlFor="emailNotifications">{t('account.notifyMe')}</label>
                                    <input type="checkbox" className="form-check-input ml-2" id="emailNotifications" defaultChecked={userDetails.isNotificationEnrolled} />
                                </div>
                                <button className="btn btn-primary mt-2" onClick={handleUpdateNotifications} disabled={loading} style={{ marginBottom: 10 }}>{t('account.updateNotifications')}</button>
                            </div>
                            {(LANGUAGE_SWITCHER_LIVE || isSuperAdmin) && (
                                <>
                                    <h3><strong>{t('language.label')}</strong></h3>
                                    <div className="form-group" style={{ marginBottom: 10 }}>
                                        <label htmlFor="language">{t('language.help')}</label>
                                        <select
                                            id="language"
                                            className="form-select mt-2"
                                            style={{ maxWidth: 300 }}
                                            value={i18n.language}
                                            onChange={(e) => handleLanguage(e.target.value)}
                                        >
                                            {LANGUAGES.map(language => (
                                                <option key={language.code} value={language.code} lang={language.tag}>{language.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}


                        </div>
                    ) : (
                        <p>{t('account.noUserDetails')}</p>
                    )}
                </div>
            </div>
        </div>
    );
}
